import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import type { CaregiverProfilePatch } from '@truecare/schemas';
import { Alert, Button, Field, Input } from '@truecare/ui';
import { api } from '../api';

/**
 * Phase 5f-a — caregiver profile edit form.
 *
 * Renders the editable subset of `Caregiver` per
 * `CaregiverProfilePatchSchema` (preferredName + phones + address +
 * selfDescription). Legal name + DOB + SIN + banking are intentionally
 * NOT editable here — those are operator-controlled (Phase 5 lock) or
 * elsewhere on /me (banking has its own section in Phase 11b).
 *
 * Backend gates this at the service layer: APPLYING + ACTIVE allowed;
 * everything else returns 403 (`caregiver_locked_for_review` per
 * caregiver.service.ts:74). The portal mirrors that — this form
 * mounts only for those two statuses; UNDER_REVIEW shows a read-only
 * card instead (handled by the parent /me page).
 */

interface Props {
  initial: CaregiverProfilePatch;
  onSuccess?: () => void;
  /** When provided, a Cancel button renders alongside Save in the form's action
   *  row (so the two align). Used by the /me profile edit-toggle. */
  onCancel?: () => void;
}

const PROVINCES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
] as const;

export function CaregiverProfileEditForm({ initial, onSuccess, onCancel }: Props) {
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  // Pre-process before validation — `<Input>` fields produce '' for
  // empty state, but the patch schema has `.min(1)` requirements that
  // reject ''. We coerce '' → null at submission time below; defaults
  // here keep '' so the inputs render controlled.
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<CaregiverProfilePatch>({
    defaultValues: {
      preferredName: initial.preferredName ?? '',
      phoneMobile: initial.phoneMobile ?? '',
      phoneAlternate: initial.phoneAlternate ?? '',
      addressLine1: initial.addressLine1 ?? '',
      addressLine2: initial.addressLine2 ?? '',
      city: initial.city ?? '',
      province: initial.province ?? 'ON',
      postalCode: initial.postalCode ?? '',
      selfDescription: initial.selfDescription ?? '',
      // '' renders the disabled placeholder for a pre-existing caregiver with no
      // gender on file; a normal (post-application) caregiver always has a value.
      gender: (initial.gender ?? '') as CaregiverProfilePatch['gender'],
      // Travel radius — always has a value (column default 25).
      maxTravelKm: initial.maxTravelKm ?? 25,
    },
  });

  function coerceEmpty<T>(value: T): T | null {
    if (typeof value === 'string' && value.trim() === '') return null;
    return value;
  }

  const patchMutation = useMutation({
    mutationFn: (body: CaregiverProfilePatch) =>
      unwrap(
        api.PATCH('/api/v1/me/caregiver/profile', {
          body,
        }),
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['caregiver', 'me'] });
      qc.invalidateQueries({ queryKey: ['caregiver', 'application-readiness'] });
      // The backend syncs User.displayName ← preferredName on this PATCH, so
      // refresh the auth-me query that drives the top-right chrome (name +
      // Profile/Sign-out). Without this the chrome shows the pre-edit name.
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      reset({
        preferredName: data.preferredName,
        phoneMobile: data.phoneMobile,
        phoneAlternate: data.phoneAlternate,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        province: data.province as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        postalCode: data.postalCode as any,
        selfDescription: data.selfDescription,
        gender: (data.gender ?? '') as CaregiverProfilePatch['gender'],
        maxTravelKm: data.maxTravelKm,
      });
      setSubmitSuccess(true);
      setSubmitError(null);
      onSuccess?.();
    },
    onError: (e) => {
      setSubmitSuccess(false);
      setSubmitError(messageOf(e, 'Could not save profile.'));
    },
  });

  const onSubmit: SubmitHandler<CaregiverProfilePatch> = (data) => {
    // Coerce '' → null so the backend Zod accepts the patch (the
    // patch schema has .min(1) on each string field; '' fails that
    // but null is .nullable().optional()).
    const cleaned: CaregiverProfilePatch = {
      preferredName: coerceEmpty(data.preferredName),
      phoneMobile: coerceEmpty(data.phoneMobile),
      phoneAlternate: coerceEmpty(data.phoneAlternate),
      addressLine1: coerceEmpty(data.addressLine1),
      addressLine2: coerceEmpty(data.addressLine2),
      city: coerceEmpty(data.city),
      province: coerceEmpty(data.province),
      postalCode: coerceEmpty(data.postalCode),
      selfDescription: coerceEmpty(data.selfDescription),
      // Only send gender when a real value is selected (the schema is a value
      // enum, not nullable — never send '' or null).
      ...(data.gender ? { gender: data.gender } : {}),
      // Travel radius — send only a valid number (registered valueAsNumber → NaN
      // if the field were ever blanked; the schema bounds 1–150).
      ...(Number.isFinite(data.maxTravelKm) ? { maxTravelKm: data.maxTravelKm } : {}),
    };
    patchMutation.mutate(cleaned);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      data-testid="caregiver-profile-edit-form"
    >
      {submitError && (
        <Alert tone="danger" title="Couldn't save profile">
          {submitError}
        </Alert>
      )}
      {submitSuccess && !isDirty && (
        <Alert tone="info" title="Profile saved">
          Your profile updates have been saved.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Preferred name" error={errors.preferredName?.message}>
          <Input
            type="text"
            data-testid="caregiver-profile-preferred-name"
            {...register('preferredName')}
          />
        </Field>

        <Field
          label="Gender"
          error={errors.gender?.message}
          hint="Used to match you to shifts requesting a specific gender. “Prefer not to say” excludes you from those."
        >
          <select
            className="h-10 rounded-md border border-border bg-white px-2 text-sm text-forest"
            data-testid="caregiver-profile-gender"
            {...register('gender')}
          >
            <option value="" disabled>
              Select…
            </option>
            <option value="FEMALE">Female</option>
            <option value="MALE">Male</option>
            <option value="NON_BINARY">Non-binary</option>
            <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
          </select>
        </Field>

        <Field label="Mobile phone" error={errors.phoneMobile?.message}>
          <Input
            type="tel"
            data-testid="caregiver-profile-phone-mobile"
            {...register('phoneMobile')}
          />
        </Field>

        <Field label="Alternate phone" error={errors.phoneAlternate?.message}>
          <Input
            type="tel"
            data-testid="caregiver-profile-phone-alternate"
            {...register('phoneAlternate')}
          />
        </Field>
      </div>

      <Field label="Address line 1" error={errors.addressLine1?.message}>
        <Input
          type="text"
          data-testid="caregiver-profile-address-line1"
          {...register('addressLine1')}
        />
      </Field>

      <Field label="Address line 2" error={errors.addressLine2?.message}>
        <Input
          type="text"
          data-testid="caregiver-profile-address-line2"
          {...register('addressLine2')}
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="City" error={errors.city?.message}>
          <Input type="text" data-testid="caregiver-profile-city" {...register('city')} />
        </Field>
        <Field label="Province" error={errors.province?.message}>
          <select
            className="h-10 rounded-md border border-border bg-white px-2 text-sm text-forest"
            data-testid="caregiver-profile-province"
            {...register('province')}
          >
            {PROVINCES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Postal code"
          error={errors.postalCode?.message}
          hint="e.g. M5V 2A8"
        >
          <Input
            type="text"
            data-testid="caregiver-profile-postal-code"
            {...register('postalCode')}
          />
        </Field>
      </div>

      <Field
        label="How far will you travel? (km)"
        hint="Maximum distance from your home address you'll accept shifts. A smaller radius means fewer offers. 1–150 km."
        error={errors.maxTravelKm?.message}
      >
        <Input
          type="number"
          min={1}
          max={150}
          step={1}
          className="md:max-w-[10rem]"
          data-testid="caregiver-profile-max-travel-km"
          {...register('maxTravelKm', { valueAsNumber: true })}
        />
      </Field>

      <Field
        label="About me"
        hint="Optional — buyers may see a short bio when you appear in their candidate list."
        error={errors.selfDescription?.message}
      >
        <textarea
          rows={4}
          className="w-full rounded-md border border-border bg-white p-2 text-sm text-forest"
          data-testid="caregiver-profile-self-description"
          {...register('selfDescription')}
        />
      </Field>

      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={isSubmitting || patchMutation.isPending}
          disabled={!isDirty}
          data-testid="caregiver-profile-save"
        >
          Save profile
        </Button>
      </div>
    </form>
  );
}
